import React from "react";
import { ShieldCheck, Crown, Gem, Star, Award, Sparkles } from "lucide-react";

/**
 * Style catalogue for premium tiers.
 * Keys mirror the backend PLANS dict in server.py.
 */
const TIERS = {
  founder: {
    label: "Founder",
    Icon: Award,
    gradient: "linear-gradient(135deg, #F59E0B 0%, #DC2626 100%)",
    ring: "0 0 0 1px rgba(220,38,38,0.35)",
    fg: "#FFF7ED",
  },
  silver: {
    label: "Silver",
    Icon: Star,
    gradient: "linear-gradient(135deg, #94A3B8 0%, #64748B 100%)",
    ring: "0 0 0 1px rgba(100,116,139,0.35)",
    fg: "#F8FAFC",
  },
  gold: {
    label: "Gold",
    Icon: Crown,
    gradient: "linear-gradient(135deg, #FCD34D 0%, #B45309 100%)",
    ring: "0 0 0 1px rgba(180,83,9,0.35)",
    fg: "#FFFBEB",
  },
  gold_plus: {
    label: "Gold+",
    Icon: Crown,
    gradient: "linear-gradient(135deg, #FBBF24 0%, #7C2D12 100%)",
    ring: "0 0 0 1px rgba(124,45,18,0.35)",
    fg: "#FFFBEB",
  },
  diamond: {
    label: "Diamond",
    Icon: Gem,
    gradient: "linear-gradient(135deg, #60A5FA 0%, #7C3AED 100%)",
    ring: "0 0 0 1px rgba(124,58,237,0.35)",
    fg: "#EFF6FF",
  },
  // Fallback: user is premium but plan is unknown (older account)
  _default: {
    label: "Premium",
    Icon: Sparkles,
    gradient: "linear-gradient(135deg, #C85A53 0%, #DB2777 100%)",
    ring: "0 0 0 1px rgba(219,39,119,0.35)",
    fg: "#FFF7ED",
  },
};

/**
 * KYC badge — deep-green shield.
 */
export function KycBadge({ compact = false, className = "" }) {
  return (
    <div
      data-testid="badge-kyc"
      className={`inline-flex items-center gap-1 text-white font-semibold rounded-full ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"} ${className}`}
      style={{
        background: "linear-gradient(135deg, #4F7942 0%, #065F46 100%)",
        boxShadow: "0 0 0 1px rgba(6,95,70,0.3), 0 4px 10px rgba(6,95,70,0.15)",
      }}
      title="Aadhaar / PAN verified"
    >
      <ShieldCheck className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} strokeWidth={2.5} />
      {compact ? "KYC" : "KYC Verified"}
    </div>
  );
}

/**
 * Premium tier badge — colour/icon vary by plan.
 * Props: `plan` = "founder" | "silver" | "gold" | "gold_plus" | "diamond" | undefined
 * Falls back to a generic "Premium" pill if plan is missing but is_premium is true.
 */
export function PremiumBadge({ plan, compact = false, className = "" }) {
  const key = plan && TIERS[plan] ? plan : "_default";
  const t = TIERS[key];
  const Icon = t.Icon;
  return (
    <div
      data-testid={`badge-plan-${key}`}
      className={`inline-flex items-center gap-1 text-white font-semibold rounded-full ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"} ${className}`}
      style={{
        background: t.gradient,
        color: t.fg,
        boxShadow: `${t.ring}, 0 4px 10px rgba(0,0,0,0.10)`,
      }}
      title={`${t.label} Member`}
    >
      <Icon className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} strokeWidth={2.5} />
      {t.label}
    </div>
  );
}

/**
 * Convenience wrapper — renders whichever badges apply for a profile.
 * Order: KYC first, then Premium tier.
 */
export function TrustBadges({ profile, compact = false, className = "" }) {
  if (!profile) return null;
  const showKyc = !!profile.kyc_verified;
  const showPremium = !!profile.is_premium;
  if (!showKyc && !showPremium) return null;
  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      {showKyc && <KycBadge compact={compact} />}
      {showPremium && <PremiumBadge plan={profile.premium_plan} compact={compact} />}
    </div>
  );
}
